import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-purchase-order-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1400px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/financials/purchase-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Purchase Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">PO Templates</h1>
            <p class="text-slate-500 text-xs mt-1">Reusable item and quantity sets for faster admin purchase orders.</p>
          </div>
          <button type="button" (click)="startNew()" class="h-9 px-3 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] flex items-center gap-2">
            <i class="ti ti-plus"></i>New Template
          </button>
        </header>

        <div class="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
          <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100">
              <p class="text-sm font-bold text-slate-900">Saved Templates</p>
            </div>
            <div class="divide-y divide-slate-100">
              @for (template of templates(); track template.id) {
                <button type="button" (click)="loadTemplate(template.id)" class="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors" [class.bg-sky-50]="form.id === template.id">
                  <p class="text-sm font-semibold text-slate-900">{{ template.name }}</p>
                  <p class="text-[11px] text-slate-500 mt-0.5">{{ template.item_count || 0 }} item(s) · {{ template.created_by_name || 'System' }}</p>
                </button>
              }
              @if (!templates().length) {
                <div class="px-4 py-12 text-center text-slate-400">
                  <i class="ti ti-template text-3xl"></i>
                  <p class="text-sm font-semibold mt-2">No templates yet.</p>
                </div>
              }
            </div>
          </section>

          <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div>
                <p class="text-sm font-bold text-slate-900">{{ form.id ? 'Edit Template' : 'New Template' }}</p>
                <p class="text-[11px] text-slate-500">Templates are admin-only and do not appear on mobile.</p>
              </div>
              <div class="flex items-center gap-2">
                @if (form.id) {
                  <button type="button" (click)="deleteTemplate()" class="h-8 px-3 rounded-md border border-rose-100 bg-rose-50 text-rose-600 text-xs font-semibold hover:bg-rose-100">
                    Delete
                  </button>
                }
                <button type="button" (click)="saveTemplate()" [disabled]="isSaving()" class="h-8 px-3 rounded-md bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-50">
                  {{ isSaving() ? 'Saving...' : 'Save Template' }}
                </button>
              </div>
            </div>

            <div class="p-4 space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label class="text-xs font-semibold text-slate-600">Template name
                  <input [(ngModel)]="form.name" placeholder="e.g. Standard bathroom rough-in" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                </label>
                <label class="text-xs font-semibold text-slate-600">Description
                  <input [(ngModel)]="form.description" placeholder="Optional" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                </label>
              </div>

              <div class="rounded-xl border border-slate-200 overflow-hidden">
                <div class="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <div class="relative flex-1 max-w-md">
                    <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input [(ngModel)]="materialSearch" (input)="searchMaterials()" placeholder="Search material" class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-white focus:outline-none focus:border-[#1A6FA6]">
                  </div>
                  <button type="button" (click)="addManualItem()" class="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2"><i class="ti ti-plus"></i>Manual row</button>
                </div>
                @if (materialResults().length > 0) {
                  <div class="px-3 py-2 border-b border-slate-100 flex gap-2 overflow-x-auto">
                    @for (mat of materialResults(); track mat.id) {
                      <button type="button" (click)="addMaterialItem(mat)" class="shrink-0 px-3 py-2 rounded-lg border border-sky-100 bg-sky-50 text-left hover:bg-sky-100">
                        <p class="text-xs font-semibold text-sky-900">{{ mat.name }}</p>
                        <p class="text-[10px] text-sky-700">{{ mat.serial_no }} · {{ mat.unit || 'EA' }}</p>
                      </button>
                    }
                  </div>
                }
                <div class="overflow-x-auto">
                  <table class="w-full text-left">
                    <thead><tr class="bg-white border-b border-slate-100">
                      <th class="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest min-w-[240px]">Item</th>
                      <th class="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-24">Qty</th>
                      <th class="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-24">Unit</th>
                      <th class="px-3 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-32">Rate</th>
                      <th class="w-10"></th>
                    </tr></thead>
                    <tbody class="divide-y divide-slate-100">
                      @for (item of form.items; track $index) {
                        <tr>
                          <td class="px-3 py-2">
                            <input [(ngModel)]="item.title" placeholder="Item title" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs font-semibold">
                            <input [(ngModel)]="item.description" placeholder="Description" class="mt-1 w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-500">
                          </td>
                          <td class="px-3 py-2"><input [(ngModel)]="item.quantity" type="number" min="0" step="0.01" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"></td>
                          <td class="px-3 py-2"><input [(ngModel)]="item.unit" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs"></td>
                          <td class="px-3 py-2"><input [(ngModel)]="item.unit_price" type="number" min="0" step="0.01" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"></td>
                          <td class="px-2 py-2"><button type="button" (click)="removeItem($index)" class="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50"><i class="ti ti-trash"></i></button></td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  `
})
export class AdminPurchaseOrderTemplatesComponent implements OnInit {
  private api = inject(ApiService);
  templates = signal<any[]>([]);
  materialResults = signal<any[]>([]);
  isSaving = signal(false);
  materialSearch = '';
  form: any = this.emptyForm();

  ngOnInit() {
    this.loadTemplates();
    this.addManualItem();
  }

  emptyForm() {
    return { id: null, name: '', description: '', items: [] };
  }

  startNew() {
    this.form = this.emptyForm();
    this.addManualItem();
  }

  loadTemplates() {
    this.api.getPurchaseOrderTemplates().subscribe({ next: (res: any) => this.templates.set(res.data?.templates || []) });
  }

  loadTemplate(id: number) {
    this.api.getPurchaseOrderTemplate(id).subscribe({
      next: (res: any) => {
        const template = res.data?.template;
        this.form = {
          id: template.id,
          name: template.name,
          description: template.description || '',
          items: (template.items || []).map((item: any) => ({
            material_id: item.material_id ? Number(item.material_id) : null,
            sku: item.sku || '',
            title: item.title || '',
            description: item.description || '',
            quantity: Number(item.quantity || 1),
            unit: item.unit || 'EA',
            unit_price: Number(item.unit_price || 0)
          }))
        };
      },
      error: () => showToast('error', 'Could not load template.')
    });
  }

  searchMaterials() {
    if (this.materialSearch.trim().length < 2) {
      this.materialResults.set([]);
      return;
    }
    this.api.getMaterials({ search: this.materialSearch, include_children: false, limit: 12 }).subscribe({
      next: (res: any) => this.materialResults.set(res.data?.materials || [])
    });
  }

  addMaterialItem(mat: any) {
    this.form.items.push({ material_id: mat.id, sku: mat.serial_no, title: mat.name, description: mat.description || '', quantity: 1, unit: mat.unit || 'EA', unit_price: Number(mat.cost_price || 0) });
    this.materialSearch = '';
    this.materialResults.set([]);
  }

  addManualItem() {
    this.form.items.push({ material_id: null, sku: '', title: '', description: '', quantity: 1, unit: 'EA', unit_price: 0 });
  }

  removeItem(index: number) {
    this.form.items.splice(index, 1);
  }

  saveTemplate() {
    if (!this.form.name?.trim()) return showToast('error', 'Template name is required.');
    if (!this.form.items.length) return showToast('error', 'Add at least one template item.');
    this.isSaving.set(true);
    this.api.savePurchaseOrderTemplate(this.form).subscribe({
      next: (res: any) => {
        showToast('success', 'Template saved.');
        this.isSaving.set(false);
        this.loadTemplates();
        this.loadTemplate(res.data.id);
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err.error?.message || 'Could not save template.');
      }
    });
  }

  deleteTemplate() {
    if (!this.form.id || !confirm('Delete this purchase order template?')) return;
    this.api.deletePurchaseOrderTemplate(this.form.id).subscribe({
      next: () => {
        showToast('success', 'Template deleted.');
        this.loadTemplates();
        this.startNew();
      },
      error: (err) => showToast('error', err.error?.message || 'Could not delete template.')
    });
  }
}
